import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export type CalendarTheme = 'light' | 'dark' | 'system'

interface CalendarThemeContextValue {
  theme: CalendarTheme
  resolvedTheme: 'light' | 'dark'
  setTheme: (theme: CalendarTheme) => void
}

const CalendarThemeContext = createContext<CalendarThemeContextValue | undefined>(undefined)

const THEME_STORAGE_KEY = 'perfectamed-calendar-theme'

interface CalendarThemeProviderProps {
  children: ReactNode
  defaultTheme?: CalendarTheme
}

export const CalendarThemeProvider: React.FC<CalendarThemeProviderProps> = ({
  children,
  defaultTheme = 'system',
}) => {
  const [theme, setThemeState] = useState<CalendarTheme>(() => {
    // Load from localStorage on mount
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(THEME_STORAGE_KEY)
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored
      }
    }
    return defaultTheme
  })

  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return 'light'
  })

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light')
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // Calculate resolved theme
  const resolvedTheme = theme === 'system' ? systemTheme : theme

  // Update document attribute
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', resolvedTheme)
    }
  }, [resolvedTheme])

  const setTheme = (newTheme: CalendarTheme) => {
    setThemeState(newTheme)
    if (typeof window !== 'undefined') {
      localStorage.setItem(THEME_STORAGE_KEY, newTheme)
    }
  }

  return (
    <CalendarThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </CalendarThemeContext.Provider>
  )
}

export const useCalendarTheme = () => {
  const context = useContext(CalendarThemeContext)
  if (context === undefined) {
    throw new Error('useCalendarTheme must be used within a CalendarThemeProvider')
  }
  return context
}
